import { email, required, confirmed, max, max_value, min_value, min } from 'vee-validate/dist/rules'
import { extend } from 'vee-validate'
import wav from 'wallet-address-validator'
import { getIsTxHash } from '@/utils/get-is-tx-hash'
import store from "@/store";

extend('email', email)

extend('max', { ...max, message: 'Max {length} characters' })

extend('min', { ...min, message: 'Min {length} characters' })

extend('max_value', max_value)
extend('min_value', min_value)

extend('required', { ...required, message: '{_field_} is required.' })

extend('confirmed', { ...confirmed, message: 'Passwords don’t match' })

extend('vSelectMultiple', {
  validate(value) {
    return value !== undefined && value !== null && Object.keys(value).length > 0;
  },
  message: 'value is not valid'
})

extend('address', {
  validate(value, { currency }) {
    const coinData =
      store?.state?.analytics?.activeCoinData ||
      store?.state?.analytics?.coinData

    if (coinData?.addressRegex) {
      return new RegExp(coinData.addressRegex).test(value)
    }

    return wav.validate(value, currency) || false
  },
  params: ['currency'],
  message: 'Wallet address is not valid'
})

extend('txHash', {
  validate(value) {
    const coinData =
      store?.state?.analytics?.activeCoinData ||
      store?.state?.analytics?.coinData

    return (
      getIsTxHash(value) ||
      (coinData?.txRegex ? new RegExp(coinData.txRegex).test(value) : false)
    )
  },
  message: 'tx hash is not valid'
})

extend('address$OR$txHash', {
  validate(value) {
    const coinData =
      store?.state?.analytics?.activeCoinData ||
      store?.state?.analytics?.coinData

    const addressValid = coinData?.addressRegex
      ? new RegExp(coinData.addressRegex).test(value)
      : false

    const txValid = coinData?.txRegex
      ? new RegExp(coinData.txRegex).test(value)
      : false

    return addressValid || txValid
  },
  message: 'Wallet address or tx hash is not valid'
})

extend('companyName', {
  validate(value) {
    return /^[a-zA-Z0-9\-.,'&() ]*$/.test(value);
  },
  message: 'Company contains invalid characters'
})

extend('addressCustomCoinRegExp', {
  validate(value, coinDataLocalRegExp ) {
    if (coinDataLocalRegExp && coinDataLocalRegExp.length) {
      coinDataLocalRegExp[0] = coinDataLocalRegExp[0].replace('$or$', '|',).replace('$comma$', ',',)
      return new RegExp(coinDataLocalRegExp).test(value)
    } else {
      return false
    }
  },
  message: 'Wallet address is not valid'
})

// Screening's one field takes either an address or a transaction hash, in the
// selected chain's format. Regexes travel as object params: in the string form
// vee-validate splits on `,` and `|`, both of which real chain formats contain.
extend('screeningValue', {
  params: ['address', 'tx', 'chain'],
  validate(value, { address, tx }) {
    const text = String(value || '').trim()

    return [tx, address].some(pattern => pattern && new RegExp(pattern).test(text))
  },
  // Pinned copy, STATE-5.5.
  message: (_, { chain }) => `The entered value is not a valid ${chain} address or transaction hash.`,
})

extend('lessThanDateRange', {
  validate(date, { time, value }) {
    const [from, to] = date
    if (value == 'day') {
      const diffInMs = Math.abs(to - from);
      const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
      return diffInDays < +time
    }
    return true
  },
  params: ['time', 'value', 'label'],
  message: 'Range must be less than {label}'
})

extend("less_than_or_equal", {
  params: ["target"],
  validate(value, { target }) {
    if (!target) return true;
    return parseFloat(value) <= parseFloat(target);
  },
  message: '{_field_} is not valid.'
});

extend("greater_than_or_equal", {
  params: ["target"],
  validate(value, { target }) {
    if (!target) return true;
    return parseFloat(value) >= parseFloat(target);
  },
  message: '{_field_} is not valid.'
});

extend("integer", {
  validate(value) {
    return /^\d+$/.test(value);
  },
  message: '{_field_} is not valid.'
});

// Same check as `integer`, with the wording the rule builder shows inside its
// sentence-style inputs, where the field name would read as noise.
extend('whole_number', {
  validate(value) {
    return /^\d+$/.test(value);
  },
  message: 'Please enter a whole number'
});

extend('asyncValid', {
  params: ['asyncFunction'],
  async validate(value, { asyncFunction }) {
    return await asyncFunction(value)
  }
})

extend('time_unit', {
  validate(value) {
    if (!value) return true;
    return /^\d+[mhdwyMHDWY]$/.test(value);
  },
  // The rule accepts a single unit — spell the examples out as alternatives so the
  // hint doesn't read as one mandatory "2w 4d 6h 45m" sequence.
  message: 'Must be in the format: 2w or 4d or 6h or 45m'
});

extend('max_time_unit', {
  params: ['maxTime'],
  validate(value, { maxTime }) {
    // An empty field is the 'required' rule's business.
    if (!value) return true;

    // "48h", "180d" → minutes. Case matters: 'm' is minutes, 'M' is months.
    const parseToMinutes = (str) => {
      const match = String(str).match(/^(\d+)([a-zA-Z]+)$/);
      if (!match) return 0;

      const val = parseInt(match[1], 10);
      const unit = match[2];

      const multipliers = {
        m: 1,
        h: 60,
        H: 60,
        d: 1440,
        D: 1440,
        w: 10080,
        W: 10080,
        M: 43200, // ~30 days
      };

      // An unknown unit is already rejected by the base 'time_unit' rule.
      return val * (multipliers[unit] || 0);
    };

    const inputMinutes = parseToMinutes(value);
    const maxMinutes = parseToMinutes(maxTime);

    return inputMinutes <= maxMinutes;
  },
  message: 'Limit: {maxTime}'
});

extend('float_number', {
  validate(value) {
    if (value === null || value === undefined || value === '') return true;
    return /^\d+(\.\d+)?$/.test(String(value));
  },
  message: 'Must be a valid number'
});

extend('percent', {
  validate(value) {
    if (value === null || value === undefined || value === '') return true;
    if (!/^\d+(\.\d+)?$/.test(String(value))) return false;
    const num = parseFloat(value);
    return num >= 0 && num <= 100;
  },
  message: 'Must be between 0 and 100'
});

// Driven by the server verdict from `users/checkOrganizationName` — the client
// never holds the list of existing names (GLBA-13245).
extend('uniqueOrganizationName', {
  params: ['taken'],
  validate(value, { taken }) {
    return !taken
  },
  message: 'Organization with this name already exist. Please contact CSM'
})
